import { validateIBAN, isValidIBAN, extractIBAN, ValidationErrorsIBAN } from 'ibantools'

/**
 * IBAN validation result
 */
export interface IbanValidationResult {
  valid: boolean
  iban: string
  formattedIban: string
  countryCode: string
  checkDigits: string
  bban: string
  errors: string[]
}

/**
 * Country-specific IBAN length definitions
 * Based on SWIFT registry and EPC guidelines
 */
const IBAN_LENGTHS: Record<string, number> = {
  AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BI: 27,
  BR: 29, BY: 28, CH: 21, CR: 22, CY: 28, CZ: 24, DE: 22, DJ: 27, DK: 18, DO: 28,
  EE: 20, EG: 29, ES: 24, FI: 18, FK: 18, FO: 18, FR: 27, GB: 22, GE: 22, GI: 23,
  GL: 18, GR: 27, GT: 28, HR: 21, HU: 28, IE: 22, IL: 23, IQ: 23, IS: 26, IT: 27,
  JO: 30, KW: 30, KZ: 20, LB: 28, LC: 32, LI: 21, LT: 20, LU: 20, LV: 21, LY: 25,
  MC: 27, MD: 24, ME: 22, MK: 19, MN: 20, MR: 27, MT: 31, MU: 30, NI: 28, NL: 18,
  NO: 15, OM: 23, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29, RO: 24, RS: 22, RU: 33,
  SA: 24, SC: 31, SD: 18, SE: 24, SI: 19, SK: 24, SM: 27, SO: 23, ST: 25, SV: 28,
  TL: 23, TN: 24, TR: 26, UA: 29, VA: 22, VG: 24, XK: 20
}

/**
 * SEPA participating countries (for RTP context)
 */
export const SEPA_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'MC', 'NL', 'NO', 'PL', 'PT',
  'RO', 'SM', 'SK', 'SI', 'ES', 'SE', 'CH', 'GB', 'GI', 'VA', 'AD', 'FO', 'GL'
])

/**
 * Maps ibantools error codes to human-readable messages
 */
function mapError(error: ValidationErrorsIBAN): string {
  switch (error) {
    case ValidationErrorsIBAN.NoIBANProvided:
      return 'No IBAN provided'
    case ValidationErrorsIBAN.NoIBANCountry:
      return 'Invalid country code'
    case ValidationErrorsIBAN.WrongBBANLength:
      return 'Invalid BBAN length for country'
    case ValidationErrorsIBAN.WrongBBANFormat:
      return 'Invalid BBAN format for country'
    case ValidationErrorsIBAN.ChecksumNotNumber:
      return 'Check digits must be numeric'
    case ValidationErrorsIBAN.WrongIBANChecksum:
      return 'Invalid IBAN checksum (mod 97)'
    case ValidationErrorsIBAN.WrongAccountBankBranchChecksum:
      return 'Invalid bank/branch checksum'
    default:
      return 'Unknown validation error'
  }
}

/**
 * Calculate IBAN checksum using ISO 7064 Mod 97-10 algorithm
 */
export function calculateIbanChecksum(iban: string): boolean {
  const cleaned = iban.replace(/\s/g, '').toUpperCase()
  if (cleaned.length < 4) return false

  // Move first 4 chars to end
  const rearranged = cleaned.slice(4) + cleaned.slice(0, 4)

  // Convert letters to numbers (A=10, B=11, ..., Z=35)
  let numericString = ''
  for (const char of rearranged) {
    if (char >= 'A' && char <= 'Z') {
      numericString += (char.charCodeAt(0) - 55).toString()
    } else {
      numericString += char
    }
  }

  // Calculate mod 97 (handle large numbers by processing in chunks)
  let remainder = 0
  for (let i = 0; i < numericString.length; i++) {
    remainder = (remainder * 10 + parseInt(numericString[i], 10)) % 97
  }

  return remainder === 1
}

/**
 * Validate an IBAN with comprehensive checks:
 * - Format validation (country-specific length/pattern)
 * - ISO 7064 Mod 97-10 checksum
 * - Country-specific BBAN validation
 */
export function validateIban(input: string): IbanValidationResult {
  const cleaned = input.replace(/\s/g, '').toUpperCase()
  const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim()

  const result: IbanValidationResult = {
    valid: false,
    iban: cleaned,
    formattedIban: formatted,
    countryCode: cleaned.slice(0, 2),
    checkDigits: cleaned.slice(2, 4),
    bban: cleaned.slice(4),
    errors: []
  }

  // Basic length check
  if (cleaned.length < 5) {
    result.errors.push('IBAN too short')
    return result
  }

  // Country code check
  if (!/^[A-Z]{2}$/.test(result.countryCode)) {
    result.errors.push('Invalid country code format')
    return result
  }

  // Check if country is known
  const expectedLength = IBAN_LENGTHS[result.countryCode]
  if (!expectedLength) {
    result.errors.push(`Unknown country code: ${result.countryCode}`)
    return result
  }

  // Length validation
  if (cleaned.length !== expectedLength) {
    result.errors.push(`Invalid length: expected ${expectedLength}, got ${cleaned.length}`)
    return result
  }

  // Use ibantools for detailed validation
  const validation = validateIBAN(cleaned)
  if (!validation.valid && validation.errorCodes) {
    result.errors = validation.errorCodes.map(mapError)
    return result
  }

  // Double-check with our own mod 97 implementation
  if (!calculateIbanChecksum(cleaned)) {
    result.errors.push('Checksum verification failed')
    return result
  }

  // All checks passed
  result.valid = true
  return result
}

/**
 * Check if IBAN is from a SEPA country
 */
export function isSepaIban(iban: string): boolean {
  const countryCode = iban.replace(/\s/g, '').toUpperCase().slice(0, 2)
  return SEPA_COUNTRIES.has(countryCode)
}

/**
 * Extract and format IBAN from messy input
 */
export function extractAndFormatIban(input: string): string | null {
  const result = extractIBAN(input)
  if (result.valid && result.iban) {
    return result.iban.replace(/(.{4})/g, '$1 ').trim()
  }
  return null
}

/**
 * Quick validation check
 */
export function isValidIban(input: string): boolean {
  return isValidIBAN(input.replace(/\s/g, '').toUpperCase())
}

